import React from 'react';
import _forEach from 'lodash/forEach';
import _filter from 'lodash/filter';
import moment from 'moment-timezone';
import { useStoreState } from '../../../models/hooks';
import { AppointmentStatus } from '../../../firebase/firestore/documents/appointmentStatus';
import {
  AvailableBookingWithPosition,
  AppointmentType,
  AppointmentAvailability,
} from '../../../firebase/firestore/documents/appointmentAvaiability';
import { useQuerySnapshot } from '../../../firebase/firestore/hooks/useQuerySnapshot';
import { FirestoreOperation } from '../../../firebase/firestore/firestoreOperation';
import { Candidate } from '../../../firebase/firestore/documents/candidate';
import { WhereCriteria } from '../../../firebase/firestore/query/criteria';
import { Collections } from '../../../firebase/firestore/collections';

export const useRequestedAdhocBookings = (
  availability?: AppointmentAvailability,
): { requestedBookings: AvailableBookingWithPosition[]; isLoading: boolean } => {
  const { calendarPresenter } = useStoreState((s) => s.calendar);

  const candidateIds = React.useMemo(() => {
    if (!availability?.bookings) return [];
    const bookingsWithCandidates = _filter(availability.bookings, (b) => !!b.candidateId);
    const ids = bookingsWithCandidates.map((b) => b.candidateId);
    return ids;
  }, [availability]);

  const queryObjects = React.useMemo(() => {
    if (candidateIds.length === 0) return null;
    return [new WhereCriteria('__name__', FirestoreOperation.IN, candidateIds.slice(0, 10))];
  }, [candidateIds]);

  const requestedCandidates = useQuerySnapshot<Candidate>(Collections.CANDIDATES, queryObjects);

  const isLoading = candidateIds.length > 0 && requestedCandidates === undefined;

  const requestedBookings = React.useMemo(() => {
    if (!calendarPresenter || !availability || !requestedCandidates) return [];

    const requestedInterviews: AvailableBookingWithPosition[] = [];

    requestedCandidates.forEach((candidate) => {
      if (candidate.dismissed) return;

      const { appointment } = candidate;
      if (appointment?.adhoc && appointment?.status === AppointmentStatus.requested) {
        let bookingId = '';
        _forEach(availability.bookings, (booking, id) => {
          if (booking.candidateId === candidate.id) {
            bookingId = id;
          }
        });

        requestedInterviews.push({
          booking: {
            id: bookingId,
            availabilityId: availability.id,
            businessId: '',
            candidateId: candidate.id,
            createdAt: availability.createdAt,
            date: availability.fromDate,
            duration: moment(availability.toDate).diff(moment(availability.fromDate), 'minutes'),
            positionId: 'positionId' in appointment ? (appointment.positionId as string) ?? '' : '',
            seekerId: candidate.seekerId,
            type: appointment.type || AppointmentType.video,
            recruiterName: calendarPresenter.getRecruiterFullNameOfBooking(availability.userId),
            recruiterId: availability.userId,
            accepted: false,
            shortCode: AppointmentStatus.requested,
          },
          candidate,
        });
      }
    });

    return requestedInterviews;
  }, [calendarPresenter, availability, requestedCandidates]);

  return { requestedBookings, isLoading };
};
